import type { ComponentType } from "react";
import type { Schema } from "@/shared/components/Form/types";
import type {
  CreateSchemaDefinition,
  EditSchemaDefinition,
} from "@/shared/components/CRUD/types";
import {
  CreateLanguageMutation,
  UpdateLanguageMutation,
  LanguageEditQuery,
  LanguageShowQuery,
  type UpdateLanguageInput,
} from "@/admin/entities/languages";
import type {
  LanguageEditQueryType,
  LanguageShowQueryType,
} from "@/types/graphql/admin.generated";

export const filterSchema: Schema = {
  search: {
    label: "Поиск",
    type: "text",
    placeholder: "Поиск по коду или названию...",
    xs: 12,
    fullWidth: true,
    priority: "primary",
  },
  withDeleted: {
    label: "Показывать удалённые",
    type: "checkbox",
    priority: "secondary",
    xs: 12,
    md: 6,
    fullWidth: true,
  },
  deleted: {
    label: "Статус",
    type: "select",
    priority: "secondary",
    xs: 12,
    md: 6,
    fullWidth: true,
    options: [
      { label: "Все активные и удалённые", value: "" },
      { label: "Только активные", value: "false" },
      { label: "Только удалённые", value: "true" },
    ],
  },
};

const mainFieldsSchema: Schema = {
  title: {
    required: true,
    label: "Название",
    type: "text",
    xs: 12,
    md: 6,
    fullWidth: true,
  },
  native: {
    required: true,
    label: "Родное название",
    type: "text",
    xs: 12,
    md: 6,
    fullWidth: true,
  },
  code: {
    required: true,
    label: "Код",
    helperText: "2-10 латинских букв, например ru",
    type: "text",
    xs: 12,
    md: 6,
    fullWidth: true,
  },
  translation: {
    label: "Участвует в переводах",
    type: "checkbox",
    xs: 12,
    md: 6,
    fullWidth: true,
  },
};

export const createSchema: CreateSchemaDefinition = {
  main: {
    label: "Основные настройки",
    createMutation: CreateLanguageMutation,
    schema: mainFieldsSchema,
  },
} as const;

export const editSchemaBase: EditSchemaDefinition = {
  details: {
    label: "Просмотр",
    type: "show" as const,
    getTitle: (data: unknown) => {
      const typed = data as LanguageShowQueryType;
      return typed?.language?.title ?? null;
    },
    showQuery: LanguageShowQuery,
  },
  edit: {
    label: "Основные настройки",
    type: "form" as const,
    getTitle: (data: unknown) => {
      const typed = data as LanguageShowQueryType;
      return typed?.language?.title ?? null;
    },
    updateMutation: UpdateLanguageMutation,
    editQuery: LanguageEditQuery,
    schema: mainFieldsSchema,
    getEditData: (data: unknown): UpdateLanguageInput => {
      const { language: l } = data as LanguageEditQueryType;
      return {
        title: l.title,
        native: l.native,
        code: l.code,
        translation: l.translation,
      };
    },
  },
} as const;

export function createEditSchema(
  showComponent: ComponentType<{ editData?: unknown; id?: string }>
): EditSchemaDefinition {
  return {
    ...editSchemaBase,
    details: {
      ...editSchemaBase.details,
      showComponent,
    },
  };
}
